import { useState } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  adminTwoFactorConfirmation,
  currentAdmin,
  resendAdminTwoFactorCode,
} from "../../../services/admin";

import { useVerificationCodeValidationSchema } from "../../utilities/validationSchema";
import {
  toastMessageError,
  toastMessageSuccess,
} from "../../utilities/commonToastMessage";
import CommonButton from "../../utilities/formElements/commonButton/CommonButton";
import InputWrapper from "../../utilities/formElements/commonInput/InputWrapper";

import ROUTES from "../../../constants/routes";
import { adminCodeVerified } from "../../../constants/commonConstants";
import ActionType from "../../../resources/enums";

import Logo from "../../../assets/images/Skydive_logo_beta.svg";

import "./AdminAuth.scss";
import { maskEmail } from "../../../util/helper";

const AdminTwoFactor = () => {
  const { t: translation } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const locationData = useLocation();

  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(useVerificationCodeValidationSchema(translation)),
  });

  const email = (locationData?.state as any)?.email;

  const onVerificationCodeSubmit = async (data: any) => {
    if (!email) {
      navigate(ROUTES.ADMINLOGIN);
      return;
    }

    setLoading(true);
    const result = await adminTwoFactorConfirmation({
      code: `${data.verificationCode}`,
      email,
    });

    if (result && result.data && result.data.message === adminCodeVerified) {
      const currentAdminData = await currentAdmin();

      dispatch({
        type: ActionType.ADMINLOGIN,
        payload: currentAdminData.data,
      });

      navigate(ROUTES.USERS);
    } else {
      toastMessageError(translation("invalid_code"), "invalidCode");
    }
    setLoading(false);
  };

  const handleSendCode = () => {
    if (!email) {
      return;
    }
    resendAdminTwoFactorCode(email);
    toastMessageSuccess(translation("we_have_resend_the_code"), "resendCode");
  };

  return (
    <div className="admin-userauth-main">
      <div className="container-fluid">
        <img src={Logo} alt="logo" className="logo" />

        <div className="userauth-form-head">
          <h2>{translation("please_verify_your_account")}</h2>
          <p className="font22 fontRegular text-primary">
            {email
              ? `We just sent an email to ${maskEmail(email)}. Please enter your code below`
              : " "}
          </p>
        </div>

        <div className="userauth-form">
          <form
            onSubmit={handleSubmit((data) => onVerificationCodeSubmit(data))}
          >
            <div className="form-group">
              <label>{translation("authentication_code")}</label>
              <InputWrapper
                control={control}
                type="text"
                inputmode="numeric"
                placeholder={translation("enter_your_code")}
                className="form-control"
                name="verificationCode"
                error={errors?.verificationCode}
              />
            </div>

            <div className="action-btn mb-0 text-center">
              <CommonButton
                className="btnC theme-btn success-btn text-uppercase mt-35"
                label={translation("next")}
                loading={loading}
                disabled={loading}
              />
            </div>
          </form>

          <p className="text-center">
            {translation("didn_get_a_code")}
            <span className="cursor-class" onClick={handleSendCode}>
              <b>{translation("send_it_again")}</b>
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminTwoFactor;
